import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../router/all_routes";
import {
  PageHeader,
  SectionCard,
  FieldLabel,
  ReadOnlyField,
  BottomActionBar,
  WarningBanner,
  useConfirm,
} from "./shared/components";
import {
  getEmployeeDetails,
  getMyResignations,
  getResignationDetails,
  getResignationPrefill,
  getUserCapabilities,
  submitShortNotice,
  normalizeSeparationKind,
  formatDate,
  formatINR,
} from "./services/eSeparation.service";

const DECLARATION =
  "I understand that by requesting short notice, the calculated amount will be recovered from my final settlement, that my existing resignation will be put on hold, and that this short notice will have its own complete approval flow and its own No-Dues. All information provided is accurate and complete.";

interface OrigData {
  request_id: string;
  emp_name: string;
  emp_no: string;
  designation: string;
  posting: string;
  wing?: string;
  place_of_posting?: string;
  // YYYY-MM-DD strings; used for display + validation.
  intimation_date: string;
  notice_end_date: string;
  // Service-bond details carried over from the resignation (read-only).
  is_bond: boolean;
  bond_type_name: string;
  training_bond_amount: number;
}

const pad = (n: number) => String(n).padStart(2, "0");

// Normalise any backend date → YYYY-MM-DD. The find-one API returns dates
// already formatted as DD-MM-YYYY; handing those to `new Date()` reads them
// month-first (09-10-2026 -> 10 Sep), which is what made the notice end date and
// the shortfall wrong — so every shape is parsed explicitly, never guessed.
const toYMD = (raw?: string): string => {
  if (!raw) return "";
  const s = String(raw).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const dmy = s.match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/);
  if (dmy) return `${dmy[3]}-${dmy[2]}-${dmy[1]}`;
  const dt = new Date(s);
  if (isNaN(dt.getTime())) return "";
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
};

const ymdToUtc = (ymd: string): number => {
  const [y, m, d] = ymd.split("-").map(Number);
  return Date.UTC(y, m - 1, d);
};

const todayYMD = (): string => {
  const t = new Date();
  return `${t.getFullYear()}-${pad(t.getMonth() + 1)}-${pad(t.getDate())}`;
};

// The day before a YYYY-MM-DD date.
const dayBefore = (ymd: string): string => {
  const dt = new Date(ymdToUtc(ymd) - 86400000);
  return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`;
};

// Whole days between the original notice end date and the new relieving date —
// the same calendar-day difference the server computes.
const shortfallBetween = (noticeEnd: string, desired: string): number =>
  Math.max(0, Math.round((ymdToUtc(noticeEnd) - ymdToUtc(desired)) / 86400000));

const ShortNotice: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const routes = all_routes;

  // The original resignation may be handed over via navigation state.
  const passedRequestId =
    (location.state as any)?.request_id || (location.state as any)?.id || "";

  const [orig, setOrig] = useState<OrigData | null>(null);
  const [basicPay, setBasicPay] = useState(0);
  const [loading, setLoading] = useState(true);
  // Why a short notice cannot be raised right now (e.g. the No-Dues form has
  // already been submitted); empty when it can.
  const [blockReason, setBlockReason] = useState("");

  // The single editable input.
  const [desiredDate, setDesiredDate] = useState("");
  const [declared, setDeclared] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { confirm, confirmDialog } = useConfirm();

  // Prefill: employee master (name/designation/dept) + the active resignation
  // (request id, intimation/notice-end dates, bond) + basic pay for the amount.
  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const [empRes, prefillRes, caps] = await Promise.all([
          getEmployeeDetails(),
          getResignationPrefill(),
          getUserCapabilities(),
        ]);
        const emp = empRes.data;
        if (active) {
          setBasicPay(Number(prefillRes.data?.basic_pay) || 0);
          if (!caps.employee.can_submit_short_notice) {
            const reason =
              caps.employee.short_notice_block_reason ||
              "A short notice cannot be raised right now.";
            setBlockReason(reason);
            toast.warning(reason);
          }
        }

        let activeId = passedRequestId;
        if (!activeId) {
          // The employee's own live RESIGNATION — never a short notice /
          // withdrawal / No-Dues row, which also appear in this list.
          const listRes = await getMyResignations();
          const found = (listRes.data || []).find(
            (r) =>
              normalizeSeparationKind(r.type) === "RESIGNATION" &&
              !["REJECTED", "WITHDRAWN", "SUPERSEDED", "CLOSED"].includes(
                String(r.status ?? "").toUpperCase()
              )
          );
          if (found) activeId = found.id;
        }

        let detail: any = {};
        if (activeId) {
          const detailRes = await getResignationDetails(activeId);
          detail = detailRes.data || {};
        }

        if (active)
          setOrig({
            request_id: detail.request_id || activeId || "",
            emp_name: emp?.emp_name || detail.emp_name || "",
            emp_no: emp?.emp_no || detail.emp_no || "",
            designation: emp?.designation || detail.designation || "",
            posting: emp?.posting || detail.posting || "",
            wing: emp?.wing || detail.wing || "",
            place_of_posting:
              emp?.place_of_posting ||
              detail.place_of_posting ||
              detail.employeeDetails?.place_of_posting ||
              "",
            intimation_date: toYMD(detail.intimation_date),
            notice_end_date: toYMD(detail.notice_end_date),
            is_bond: detail.is_bond === "Y" || detail.is_bond === true,
            bond_type_name: detail.bond_type_name || "",
            training_bond_amount: Number(detail.training_bond_amount) || 0,
          });
      } catch (err) {
        console.log("Error loading active resignation for short notice:", err);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [passedRequestId]);

  const today = todayYMD();
  // The new relieving date can be any day from today up to the day BEFORE the
  // original notice end date (on the end date itself there is nothing to shorten).
  const minDate = useMemo(() => {
    if (!orig) return today;
    return orig.intimation_date && orig.intimation_date > today ? orig.intimation_date : today;
  }, [orig, today]);
  const maxDate = orig?.notice_end_date ? dayBefore(orig.notice_end_date) : undefined;
  const noWindow = !!maxDate && maxDate < minDate;
  const blocked = !!blockReason;

  const shortfallDays = useMemo(
    () =>
      desiredDate && orig?.notice_end_date
        ? shortfallBetween(orig.notice_end_date, desiredDate)
        : 0,
    [desiredDate, orig]
  );

  // (Basic Pay + DA) / 30 × Shortfall Days. DA has no synced source yet, so it
  // is treated as 0 — mirrors the server's computeShortNoticeAmount, which is
  // always the authoritative value.
  const shortNoticeAmount = useMemo(
    () =>
      shortfallDays > 0
        ? Math.round(((basicPay || 0) / 30) * shortfallDays * 100) / 100
        : 0,
    [basicPay, shortfallDays]
  );

  const openOriginal = () => {
    if (!orig?.request_id) return;
    navigate(routes.Esepration.approvalDetail.replace(":id", orig.request_id), {
      state: {
        request_id: orig.request_id,
        type: "Intimation",
        role: "Employee",
        viewOnly: true,
      },
    });
  };

  const handleCancel = async () => {
    if (
      await confirm("Discard changes?", {
        title: "Cancel",
        confirmLabel: "Discard",
        tone: "danger",
      })
    )
      navigate(-1);
  };

  const handleSubmit = async () => {
    if (blocked) return toast.warning(blockReason);
    if (!orig?.request_id)
      return toast.warning(
        "A short notice can only be raised against an existing resignation."
      );
    if (!desiredDate)
      return toast.warning("Please select your desired relieving date.");
    if (desiredDate < today)
      return toast.warning("Desired relieving date cannot be in the past.");
    if (orig.intimation_date && desiredDate < orig.intimation_date)
      return toast.warning(
        "Desired relieving date cannot be earlier than your resignation date."
      );
    if (orig.notice_end_date && desiredDate >= orig.notice_end_date)
      return toast.warning(
        "Desired relieving date must be earlier than the original notice end date."
      );
    if (!declared)
      return toast.warning("Please accept the declaration before submitting.");

    if (
      !(await confirm(
        "Your existing resignation will be put on hold wherever it has reached, and this short notice will go through its own complete approval flow, with its own No-Dues. Once raised, it cannot be undone except by rejection. Continue?",
        { title: "Submit Short Notice", confirmLabel: "Submit", tone: "primary" }
      ))
    )
      return;

    setSubmitting(true);
    const res = await submitShortNotice({
      request_id: orig.request_id,
      desired_relieve_date: desiredDate,
      reason: "",
    });
    setSubmitting(false);
    if (res.success) {
      toast.success(
        "Your short notice request has been submitted. Your resignation is on hold and the short notice has started its own approval flow."
      );
      navigate(routes.Esepration.overview);
    } else {
      toast.error(res.message || "Failed to submit. Please try again.");
    }
  };

  const Hint: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <small className="text-muted d-block mt-1" style={{ fontSize: 11 }}>
      {children}
    </small>
  );

  return (
    <div className="page-wrapper">
      <div className="content pb-0">
        <PageHeader title="Short Notice Resignation" onExit={() => navigate(-1)} />

        {/* Intro band */}
        <div
          className="d-flex align-items-start gap-3 pb-3 mb-4"
          style={{ borderBottom: "1px solid rgba(0,0,0,0.07)" }}
        >
          <span
            className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
            style={{ width: 36, height: 36, borderRadius: 10, background: "#FFF3EC", color: "#BA7517" }}
          >
            <i className="ti ti-clock" style={{ fontSize: 18 }} />
          </span>
          <div>
            <div className="fw-semibold mb-1">Short Notice Resignation</div>
            <p className="text-muted mb-0 small">
              You have already resigned and now wish to be relieved earlier. All
              details are carried over from your existing resignation — you only
              choose the new relieving date. Your resignation is put on hold
              wherever it has reached, and this short notice runs its own
              complete flow — the full approval hierarchy, Competent Authority
              approval and its own No-Dues. A short notice can only be raised
              before you submit your No-Dues form.
            </p>
          </div>
        </div>

        {blocked && <WarningBanner type="error" message={blockReason} />}

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-warning" role="status" />
            <p className="text-muted mt-3 mb-0">Loading your resignation...</p>
          </div>
        ) : (
          <>
            {/* Section 1 — Original Resignation Details (inherited, read-only) */}
            <SectionCard index={0} title="Original Resignation Details">
              <WarningBanner
                type="info"
                message="These details are inherited from your original resignation and cannot be edited here."
              />
              <div className="mb-3">
                <FieldLabel label="Employee Name" />
                <ReadOnlyField value={orig?.emp_name || "—"} />
              </div>
              <div className="mb-3">
                <FieldLabel label="Employee No." />
                <ReadOnlyField value={orig?.emp_no || "—"} />
              </div>
              <div className="mb-3">
                <FieldLabel label="Designation" />
                <ReadOnlyField value={orig?.designation || "—"} />
              </div>
              <div className="mb-3">
                <FieldLabel label="Department" />
                <ReadOnlyField value={orig?.posting || "—"} />
              </div>
              <div className="mb-3">
                <FieldLabel label="Place of Posting" />
                <ReadOnlyField value={orig?.place_of_posting || "—"} />
              </div>
              <div className="mb-3">
                <FieldLabel label="Wing" />
                <ReadOnlyField value={orig?.wing || "—"} />
              </div>
              <div className="mb-3">
                <FieldLabel label="Resignation Request" />
                <ReadOnlyField value={orig?.request_id || "—"} />
              </div>
              <div className="mb-3">
                <FieldLabel label="Original Resignation Date" />
                <ReadOnlyField value={orig?.intimation_date ? formatDate(orig.intimation_date) : "—"} />
              </div>
              <div className="mb-0">
                <FieldLabel label="Original Notice End Date" />
                <ReadOnlyField value={orig?.notice_end_date ? formatDate(orig.notice_end_date) : "—"} />
              </div>
              {orig?.request_id && (
                <div className="mt-3">
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    onClick={openOriginal}
                  >
                    <i className="ti ti-file-text me-1" />
                    View Resignation Form
                  </button>
                </div>
              )}
            </SectionCard>

            {/* Section 2 — New Relieving Date (the only editable input) */}
            <SectionCard index={1} title="New Relieving Date">
              {noWindow && (
                <WarningBanner
                  type="error"
                  message="There is no day left before your notice period ends, so a short notice cannot be raised now."
                />
              )}
              <div className="mb-3">
                <FieldLabel label="Now Wish to be Relieved On" required />
                <div className="position-relative">
                  <div
                    className="d-flex align-items-center justify-content-between form-control"
                    style={{ height: 44, borderRadius: 10, cursor: "pointer" }}
                  >
                    <span style={{ color: desiredDate ? "#1a1f36" : "#9aa0a6" }}>
                      {desiredDate ? formatDate(desiredDate) : "Select desired relieving date"}
                    </span>
                    <i className="ti ti-calendar" style={{ color: "#9aa0a6", fontSize: 16 }} />
                  </div>
                  <input
                    type="date"
                    min={minDate}
                    max={maxDate}
                    disabled={noWindow || blocked}
                    value={desiredDate}
                    onChange={(e) => setDesiredDate(e.target.value)}
                    style={{
                      position: "absolute",
                      inset: 0,
                      width: "100%",
                      height: "100%",
                      opacity: 0,
                      cursor: "pointer",
                    }}
                  />
                </div>
                <Hint>
                  Choose a date from today
                  {orig?.notice_end_date
                    ? ` up to the day before your original notice end date (${formatDate(orig.notice_end_date)})`
                    : ""}
                  .
                </Hint>
              </div>
              <div className="mb-0">
                <FieldLabel label="Shortfall Days" />
                <ReadOnlyField value={shortfallDays > 0 ? `${shortfallDays} days` : "—"} />
                <Hint>
                  Auto-calculated: days between your new relieving date and the
                  original notice end date.
                </Hint>
              </div>
            </SectionCard>

            {/* Section 3 — Bond & Short Notice Period Amount */}
            <SectionCard index={2} title="Bond & Short Notice Amount">
              <div className="mb-3">
                <FieldLabel label="Service Bond" />
                <ReadOnlyField
                  value={
                    orig?.is_bond
                      ? `Yes${orig.bond_type_name ? ` — ${orig.bond_type_name}` : ""}`
                      : "No"
                  }
                />
                <Hint>Carried over from your resignation.</Hint>
              </div>
              {orig?.is_bond && (
                <div className="mb-3">
                  <FieldLabel label="Training Cost" />
                  <ReadOnlyField value={formatINR(orig.training_bond_amount)} />
                </div>
              )}
              <div className="mb-0">
                <FieldLabel label="Short Notice Period Amount" />
                <ReadOnlyField value={shortNoticeAmount > 0 ? formatINR(shortNoticeAmount) : "—"} />
                {/* <Hint>
                  Auto-calculated as (Basic Pay + DA) ÷ 30 × shortfall days — the
                  same rule used on your resignation. It updates when you change
                  the relieving date.
                </Hint> */}
              </div>
            </SectionCard>

            {/* Declaration */}
            <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: 16 }}>
              <div className="card-body">
                <div
                  className="d-flex align-items-start gap-2"
                  style={{ cursor: "pointer" }}
                  onClick={() => setDeclared((d) => !d)}
                >
                  <input
                    type="checkbox"
                    className="form-check-input mt-1 flex-shrink-0"
                    id="declaration"
                    checked={declared}
                    onChange={(e) => setDeclared(e.target.checked)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <label
                    className="form-check-label small text-muted"
                    htmlFor="declaration"
                    style={{ cursor: "pointer", lineHeight: 1.5 }}
                  >
                    {DECLARATION}
                  </label>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {confirmDialog}

      <BottomActionBar
        primaryLabel="Submit Short Notice"
        primaryIcon="ti-send"
        secondaryLabel="Cancel"
        onPrimary={handleSubmit}
        onSecondary={handleCancel}
        disabled={!declared || submitting || loading || noWindow || !desiredDate || blocked}
        note={blocked ? blockReason : undefined}
      />
    </div>
  );
};

export default ShortNotice;
